import { useMemo } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { CSSProperties } from "react";

interface GearProps {
  size: number;
  /** Seconds for one full rotation. */
  spin: number;
  reverse?: boolean;
  /** Opacity of the brass cog (0–1). */
  opacity?: number;
  /** Number of teeth — vary per gear so the mechanism reads as real. */
  teeth?: number;
  /** Spokes connecting hub to rim. */
  spokes?: number;
  /** Discrete "lurch" angle applied on top of the idle spin. */
  kick?: number;
  /** Absolute positioning (left/right/top/bottom). */
  position: CSSProperties;
}

/* viewBox is -50…50 (100×100), centred on the axle. */
const R_OUTER = 49; // tooth tip
const R_ROOT = 40; // tooth root / rim
const R_HOLE = 23; // inner edge of the rim (annulus)
const R_HUB = 13; // hub disc
const R_AXLE = 4.5; // centre hole

/** Trapezoidal cog silhouette: 4 vertices per tooth, gap along the root. */
function cogPath(teeth: number): string {
  const step = (Math.PI * 2) / teeth;
  const at = (a: number, r: number) => `${(r * Math.cos(a)).toFixed(2)} ${(r * Math.sin(a)).toFixed(2)}`;
  let d = "";
  for (let i = 0; i < teeth; i++) {
    const b = i * step;
    d += `${i === 0 ? "M" : "L"} ${at(b, R_ROOT)} `;
    d += `L ${at(b + step * 0.13, R_OUTER)} `;
    d += `L ${at(b + step * 0.37, R_OUTER)} `;
    d += `L ${at(b + step * 0.5, R_ROOT)} `;
  }
  d += "Z";
  // Carve the centre out with an evenodd inner circle → a toothed ring.
  d += ` M ${R_HOLE} 0 A ${R_HOLE} ${R_HOLE} 0 1 1 ${-R_HOLE} 0 A ${R_HOLE} ${R_HOLE} 0 1 1 ${R_HOLE} 0 Z`;
  return d;
}

/**
 * A background clock gear: idle continuous rotation + a springy "kick" that
 * fires whenever the viewed section changes (the mechanism reacting to you).
 */
export function Gear({ size, spin, reverse, opacity = 0.26, teeth = 12, spokes = 5, kick = 0, position }: GearProps) {
  const reduced = useReducedMotion();
  const ring = useMemo(() => cogPath(teeth), [teeth]);

  return (
    <motion.div
      style={{ position: "absolute", width: size, height: size, ...position }}
      animate={reduced ? undefined : { rotate: reverse ? -360 : 360 }}
      transition={reduced ? undefined : { duration: spin, ease: "linear", repeat: Infinity }}
    >
      <motion.div
        style={{ position: "absolute", inset: 0 }}
        animate={{ rotate: reduced ? 0 : kick }}
        transition={{ type: "spring", stiffness: 90, damping: 12 }}
      >
        <svg viewBox="-50 -50 100 100" width={size} height={size} style={{ display: "block", color: "var(--tc-brass)" }}>
          <g fill="currentColor" fillOpacity={opacity}>
            {/* toothed ring */}
            <path d={ring} fillRule="evenodd" />
            {/* spokes */}
            {Array.from({ length: spokes }).map((_, i) => (
              <rect
                key={i}
                x={-2.6}
                y={-(R_HOLE + 1)}
                width={5.2}
                height={R_HOLE - R_HUB + 2}
                rx={1.5}
                transform={`rotate(${(360 / spokes) * i})`}
              />
            ))}
            {/* hub */}
            <circle r={R_HUB} />
          </g>
          {/* axle hole — punched through to the page so it reads as a wheel */}
          <circle r={R_AXLE} fill="var(--tc-paper-2)" fillOpacity={Math.min(1, opacity + 0.4)} />
        </svg>
      </motion.div>
    </motion.div>
  );
}
